import { Platform } from 'react-native';
import { getItem, removeItem, setItem } from '@/src/lib/storage';
import { isReferralCodeShape, normalizeReferralCode } from '@/src/components/referrals/offerCatalogue';
import { INSTALL_REFERRER_CODE_KEY, INSTALL_REFERRER_TIME_KEY } from '@/src/config/appStores';

/**
 * Deferred referral code on the device (ALL-22).
 *
 * A share link can reach a phone that does not have the app yet: the visitor
 * installs it and registers later. The native app keeps the indications it saw
 * (a share link opened in the app, or the Android Play install referrer) so the
 * registration screens can prefill one. This is only a prefill: the signup field
 * still validates it with the server and attribution still freezes at signup
 * completion (D-057). Nothing here attributes, charges or grants anything.
 *
 * D-057 "last VALID indication wins": an indication is first kept as an
 * unvalidated HINT. Only after the server's read-only eligibility check accepts
 * it does it replace the ACCEPTED record, so a newer unknown, suspended or
 * closed code can never destroy an earlier valid one; while the server cannot
 * answer, the hint waits and the accepted record stays in use.
 *
 * A captured indication is valid for the server's link validity (Admin, 30 days
 * by default) before registration completes; an older one is dropped. The
 * server enforces the same window from `capturedAt` at signup (D-057); the
 * local copy of the value only keeps storage tidy. Web never stores anything:
 * the browser flow keeps carrying `?ref=` exactly as before (ALL-10/ALL-13).
 *
 * Writes go through the shared storage wrapper, which swallows native errors,
 * so every write here is read back and a mismatch raises
 * {@link ReferralStorageError} instead of reporting a code that is not stored.
 */
export const PENDING_REFERRAL_KEY = '@alldrivers/pending_referral_v1';
export const REFERRAL_HINTS_KEY = '@alldrivers/referral_hints_v1';
export const INSTALL_REFERRER_CHECKED_KEY = '@alldrivers/install_referrer_checked_v1';
export const REFERRAL_LINK_VALIDITY_KEY = '@alldrivers/referral_link_validity_days_v1';
export const PENDING_REFERRAL_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const MAX_HINTS = 5;
const CLOCK_SKEW_MS = 60_000;

export type PendingReferralSource = 'link' | 'install_referrer';
export type ReferralEligibility = 'valid' | 'invalid' | 'unavailable';
export type ReferralEligibilityCheck = (code: string, capturedAt?: string) => Promise<ReferralEligibility>;

export interface PendingReferral {
  code: string;
  source: PendingReferralSource;
  capturedAt: string;
}

export class ReferralStorageError extends Error {
  constructor(key: string) {
    super(`Referral storage write was not persisted: ${key}`);
    this.name = 'ReferralStorageError';
  }
}

const isNative = () => Platform.OS !== 'web';

async function writeDurably(key: string, value: string): Promise<void> {
  await setItem(key, value);
  if ((await getItem(key)) !== value) throw new ReferralStorageError(key);
}

async function removeDurably(key: string): Promise<void> {
  await removeItem(key);
  if ((await getItem(key)) !== null) throw new ReferralStorageError(key);
}

/** Remember the server's link validity (days) for local expiry of stored indications. */
export async function saveReferralLinkValidityDays(days: unknown): Promise<void> {
  if (!isNative() || typeof days !== 'number' || !Number.isInteger(days) || days < 1) return;
  try { await setItem(REFERRAL_LINK_VALIDITY_KEY, String(days)); } catch { /* the default window still applies */ }
}

async function linkValidityMs(): Promise<number> {
  try {
    const days = Number(await getItem(REFERRAL_LINK_VALIDITY_KEY));
    return Number.isInteger(days) && days >= 1 ? days * DAY_MS : PENDING_REFERRAL_TTL_MS;
  } catch {
    return PENDING_REFERRAL_TTL_MS;
  }
}

function validRecord(value: unknown, now: Date, ttlMs: number = PENDING_REFERRAL_TTL_MS): PendingReferral | null {
  if (!value || typeof value !== 'object') return null;
  const record = value as Partial<PendingReferral>;
  const code = normalizeReferralCode(String(record.code || ''));
  const captured = Date.parse(String(record.capturedAt));
  const source: PendingReferralSource = record.source === 'install_referrer' ? 'install_referrer' : 'link';
  if (!isReferralCodeShape(code) || !Number.isFinite(captured)) return null;
  if (now.getTime() - captured > ttlMs || captured - now.getTime() > CLOCK_SKEW_MS) return null;
  return { code, source, capturedAt: new Date(captured).toISOString() };
}

const newestFirst = (a: PendingReferral, b: PendingReferral) => Date.parse(b.capturedAt) - Date.parse(a.capturedAt);

/** The last server-accepted indication, or null (an expired/corrupt one is dropped). */
export async function loadPendingReferral(now: Date = new Date()): Promise<PendingReferral | null> {
  if (!isNative()) return null;
  const raw = await getItem(PENDING_REFERRAL_KEY);
  if (!raw) return null;
  const ttlMs = await linkValidityMs();
  let record: PendingReferral | null = null;
  try { record = validRecord(JSON.parse(raw), now, ttlMs); } catch { record = null; }
  if (!record) await removeItem(PENDING_REFERRAL_KEY);
  return record;
}

/** Unvalidated indications still waiting for the eligibility check, newest first. */
export async function loadReferralHints(now: Date = new Date()): Promise<PendingReferral[]> {
  if (!isNative()) return [];
  const raw = await getItem(REFERRAL_HINTS_KEY);
  if (!raw) return [];
  const ttlMs = await linkValidityMs();
  try {
    const list = JSON.parse(raw);
    if (!Array.isArray(list)) return [];
    return list.map(item => validRecord(item, now, ttlMs)).filter((item): item is PendingReferral => !!item).sort(newestFirst);
  } catch {
    return [];
  }
}

async function saveHints(hints: PendingReferral[]): Promise<void> {
  if (hints.length === 0) {
    await removeDurably(REFERRAL_HINTS_KEY);
    return;
  }
  await writeDurably(REFERRAL_HINTS_KEY, JSON.stringify([...hints].sort(newestFirst).slice(0, MAX_HINTS)));
}

/**
 * Keep an indication as an unvalidated hint. Never touches the accepted record.
 * Throws {@link ReferralStorageError} when the hint could not be persisted.
 */
export async function recordReferralHint(
  code: string,
  source: PendingReferralSource,
  capturedAt: Date = new Date(),
  now: Date = new Date(),
): Promise<PendingReferral | null> {
  const normalized = normalizeReferralCode(code);
  if (!isNative() || !isReferralCodeShape(normalized)) return null;
  const hint: PendingReferral = { code: normalized, source, capturedAt: capturedAt.toISOString() };
  const others = (await loadReferralHints(now)).filter(item => !(item.code === hint.code && item.source === source));
  await saveHints([hint, ...others]);
  return hint;
}

export interface ResolveOptions {
  /**
   * Polled before every server check and before any write. Once it returns
   * false (deadline passed, the person navigated on or signed up) the pass stops
   * and writes NOTHING, so a late answer can never resurrect or replace a record
   * after the screen that asked has moved on.
   */
  shouldContinue?: () => boolean;
}

export interface ResolvedReferral {
  /** Last server-accepted indication (still inside the window). */
  accepted: PendingReferral | null;
  /** Hints the server could not check yet (outage), newest first. */
  unverified: PendingReferral[];
  /** Codes the server rejected in this pass; they were dropped. */
  rejected: string[];
  /** False when the pass was superseded: nothing was written. */
  committed: boolean;
}

/**
 * Check every waiting hint with the server. A valid hint replaces the accepted
 * record only when it is the newer indication; a rejected hint is dropped and
 * never displaces anything; an unchecked hint stays for the next attempt.
 * Storage failures propagate, leaving the previous records in place.
 */
export async function resolvePendingReferral(
  check: ReferralEligibilityCheck,
  now: Date = new Date(),
  { shouldContinue = () => true }: ResolveOptions = {},
): Promise<ResolvedReferral> {
  if (!isNative()) return { accepted: null, unverified: [], rejected: [], committed: true };
  const previous = await loadPendingReferral(now);
  let accepted = previous;
  const unverified: PendingReferral[] = [];
  const rejected: string[] = [];
  for (const hint of await loadReferralHints(now)) {
    if (!shouldContinue()) return { accepted: previous, unverified: [], rejected: [], committed: false };
    if (accepted && hint.code === accepted.code) {
      // Same code seen again: it stays accepted, with the newer capture time.
      if (Date.parse(hint.capturedAt) > Date.parse(accepted.capturedAt)) accepted = { ...hint };
      continue;
    }
    let result: ReferralEligibility;
    try { result = await check(hint.code, hint.capturedAt); } catch { result = 'unavailable'; }
    if (result === 'valid') {
      if (!accepted || Date.parse(hint.capturedAt) > Date.parse(accepted.capturedAt)) accepted = hint;
    } else if (result === 'invalid') {
      rejected.push(hint.code);
    } else {
      unverified.push(hint);
    }
  }
  if (!shouldContinue()) return { accepted: previous, unverified: [], rejected: [], committed: false };
  if (accepted && accepted !== previous) await writeDurably(PENDING_REFERRAL_KEY, JSON.stringify(accepted));
  await saveHints(unverified);
  return { accepted, unverified, rejected, committed: true };
}

/**
 * When the stored indication for `code` was captured (accepted record first), so
 * the signup can report it and the server applies the link validity (D-057).
 */
export async function storedReferralCapturedAt(code: string, now: Date = new Date()): Promise<string | null> {
  const normalized = normalizeReferralCode(code);
  if (!isNative() || !normalized) return null;
  const accepted = await loadPendingReferral(now);
  if (accepted?.code === normalized) return accepted.capturedAt;
  return (await loadReferralHints(now)).find(hint => hint.code === normalized)?.capturedAt ?? null;
}

/** Called once an account exists: a later signup on this device starts clean. */
export async function clearPendingReferral(): Promise<void> {
  if (!isNative()) return;
  try {
    await removeItem(PENDING_REFERRAL_KEY);
    await removeItem(REFERRAL_HINTS_KEY);
  } catch {
    // A storage failure must never affect the registration that just succeeded.
  }
}

export interface InstallReferral {
  code: string;
  /** Store-link click time when the referrer carried one (`t=` epoch seconds). */
  clickedAt: Date | null;
}

/**
 * Referral code from a Play install referrer string such as
 * `ref=CODE&t=1792576800` or `utm_source=google-play&utm_medium=organic`
 * (organic → null).
 */
export function parseInstallReferrer(raw: string | null | undefined): InstallReferral | null {
  if (!raw || typeof raw !== 'string') return null;
  let value = raw.trim();
  try {
    // The Play Store may hand the referrer back still percent-encoded.
    if (/%3D/i.test(value) && !value.includes('=')) value = decodeURIComponent(value);
  } catch {
    return null;
  }
  const params = new URLSearchParams(value);
  const code = normalizeReferralCode(params.get(INSTALL_REFERRER_CODE_KEY) || '');
  if (!isReferralCodeShape(code)) return null;
  const seconds = Number(params.get(INSTALL_REFERRER_TIME_KEY));
  const clickedAt = /^[0-9]{9,11}$/.test(params.get(INSTALL_REFERRER_TIME_KEY) || '') && Number.isFinite(seconds)
    ? new Date(seconds * 1000) : null;
  return { code, clickedAt };
}

type InstallReferrerReader = () => Promise<string>;

function defaultInstallReferrerReader(): InstallReferrerReader | null {
  try {
    // Lazy and guarded: `expo-application` is already linked in the native
    // builds (through expo-notifications), but an OTA bundle must never crash a
    // binary that lacks the module.
    const application = require('expo-application');
    return typeof application?.getInstallReferrerAsync === 'function'
      ? application.getInstallReferrerAsync
      : null;
  } catch {
    return null;
  }
}

/**
 * True while an Android install still has an unread Play install referrer that
 * this build can read: a LOCAL source whose answer is not known yet (unlike a
 * slow server check). Never throws; any doubt answers false.
 */
export async function isInstallReferrerPending(
  reader: InstallReferrerReader | null = defaultInstallReferrerReader(),
): Promise<boolean> {
  if (Platform.OS !== 'android' || !reader) return false;
  try {
    return !(await getItem(INSTALL_REFERRER_CHECKED_KEY));
  } catch {
    return false;
  }
}

/**
 * Android only: read the Play install referrer once per install and keep its
 * referral code as an unvalidated hint (the eligibility check then decides,
 * whatever else is stored). The window starts at the store-link click when the
 * referrer carries it (a future click is clamped to now); without one, at this
 * first read. The read is marked done only AFTER an actionable code has been
 * durably stored, so a failed write is retried on a later launch; organic,
 * malformed and already-expired referrers are marked done straight away.
 * Never throws.
 */
export async function captureInstallReferrerOnce(
  reader: InstallReferrerReader | null = defaultInstallReferrerReader(),
  now: Date = new Date(),
  { shouldContinue = () => true }: ResolveOptions = {},
): Promise<PendingReferral | null> {
  if (Platform.OS !== 'android') return null;
  try {
    if (await getItem(INSTALL_REFERRER_CHECKED_KEY)) return null;
    if (!reader) return null;
    const raw = await reader();
    // Superseded while reading: leave the referrer unread for the next launch.
    if (!shouldContinue()) return null;
    const referral = parseInstallReferrer(raw);
    const clicked = referral?.clickedAt && referral.clickedAt.getTime() < now.getTime() ? referral.clickedAt : now;
    const actionable = !!referral && now.getTime() - clicked.getTime() <= PENDING_REFERRAL_TTL_MS;
    const hint = actionable ? await recordReferralHint(referral!.code, 'install_referrer', clicked, now) : null;
    await writeDurably(INSTALL_REFERRER_CHECKED_KEY, now.toISOString());
    return hint;
  } catch {
    return null;
  }
}
